const EXPRESS_MOBILE_QUERY = '(max-width: 63.99rem)';
const EXPRESS_VERTICAL_CLASS = 'additional-checkout-buttons--vertical';
const EXPRESS_INERT_TARGETS =
  '.drawer__header, .cart-drawer__usp, cart-drawer-items, .drawer__footer, .drawer__inner-empty';

/*
  Mobile express checkout inside the cart drawer.
  Shopify renders one wallet-button element. Desktop leaves it in the footer.
  Mobile moves it into the dialog. Shopify reads the vertical layout only when
  that element is created, so crossing the breakpoint builds a new one with the
  same attributes. A cart refresh can insert a second copy; the one already
  hosting the buttons is kept.
*/
class CartExpressCheckout {
  constructor(drawer) {
    this.drawer = drawer;
    this.placing = false;
    this.returnFocus = null;
    this.modalTemplate = drawer.querySelector('[data-cart-express-modal]')?.cloneNode(true) || null;
    this.triggerTemplate = drawer.querySelector('[data-cart-express-open]')?.cloneNode(true) || null;
    this.media = window.matchMedia(EXPRESS_MOBILE_QUERY);
    this.onMediaChange = () => this.sync();
    this.media.addEventListener('change', this.onMediaChange);
    this.observer = new MutationObserver(() => this.onDomChange());
    this.sync();
    this.observer.observe(drawer, { childList: true, subtree: true });
  }

  destroy() {
    this.observer.disconnect();
    this.media.removeEventListener('change', this.onMediaChange);
  }

  get mobile() {
    return this.media.matches;
  }

  get modal() {
    return this.drawer.querySelector('[data-cart-express-modal]');
  }

  get isOpen() {
    return Boolean(this.modal?.classList.contains('is-open'));
  }

  handleEscape() {
    if (!this.isOpen) return false;
    this.close();
    return true;
  }

  handleClick(event) {
    const opener = event.target.closest('[data-cart-express-open]');
    if (opener && this.drawer.contains(opener)) {
      event.preventDefault();
      this.open(opener);
      return true;
    }
    if (!this.isOpen || !this.isOutsideCard(event)) return false;
    event.preventDefault();
    this.close();
    return true;
  }

  isOutsideCard(event) {
    const modal = this.modal;
    return (
      event.target.closest('[data-cart-express-close]') ||
      event.target.classList?.contains('drawer__inner') ||
      (modal?.contains(event.target) && !event.target.closest('.cart-express__panel'))
    );
  }

  /*
    Footer swaps drop `inert` on the new cart UI and can render a second
    wallet node. Re-home the buttons and, while the dialog is open, keep
    focus inside it.
  */
  onDomChange() {
    if (this.placing) return;
    this.sync();
    if (!this.isOpen) return;
    if (this.drawer.classList.contains('is-empty')) {
      this.close();
      return;
    }
    this.setInert(true);
    this.focusModal();
  }

  sync() {
    if (this.placing) return;

    this.placing = true;
    if (!this.drawer.querySelector('[data-cart-express-mount]')) this.restoreModal();
    const mount = this.drawer.querySelector('[data-cart-express-mount]');
    if (!mount) {
      this.placing = false;
      return;
    }

    let home = this.drawer.querySelector('[data-cart-express-home]');
    if (!home) home = this.restoreExpressChrome();
    const nodes = [...this.drawer.querySelectorAll('[data-cart-express-buttons]')];
    if (!home || !nodes.length) {
      this.placing = false;
      return;
    }

    const mobile = this.mobile;
    const keep = this.walletNode(nodes);
    const wasVertical = keep.classList.contains(EXPRESS_VERTICAL_CLASS);

    nodes.forEach((node) => {
      if (node !== keep) node.remove();
    });
    keep.classList.toggle('cart-express__buttons', mobile);
    keep.classList.toggle(EXPRESS_VERTICAL_CLASS, mobile);
    this.drawer.querySelector('[data-cart-express-open]')?.toggleAttribute('inert', !mobile);
    if (!mobile && this.isOpen) this.close();

    const target = mobile ? mount : home;
    const layoutChanged = wasVertical !== mobile || keep.parentElement !== target;
    if (keep.parentElement !== target) target.appendChild(keep);
    if (layoutChanged) this.rebuildWallet(keep);
    this.placing = false;
  }

  /*
    Emptying the cart replaces the whole panel. A section response with
    express checkout off does not include the dialog, and later adds only
    refresh the footer. Put the closed dialog back first.
  */
  restoreModal() {
    const inner = this.drawer.querySelector('.drawer__inner');
    if (!inner || !this.modalTemplate) return;
    inner.appendChild(this.modalTemplate.cloneNode(true));
    inner.classList.remove('is-express-open');
    inner.style.removeProperty('--cart-express-shift');
  }

  /*
    A cart refresh can render the footer with express checkout off. The
    wrapper still has the setting, so put the button back. Keep the wallet
    node that already hosts the buttons; otherwise adopt the new footer copy.
  */
  restoreExpressChrome() {
    const ctas = this.drawer.querySelector('.drawer__footer .cart__ctas');
    if (!ctas) return null;

    const liveNodes = this.drawer.querySelectorAll('[data-cart-express-buttons]');
    const plain = [...ctas.querySelectorAll('.cart__dynamic-checkout-buttons')].filter(
      (node) => !node.hasAttribute('data-cart-express-buttons')
    );
    const home = document.createElement('div');
    home.setAttribute('data-cart-express-home', '');

    if (liveNodes.length) {
      plain.forEach((node) => node.remove());
    } else if (plain[0]) {
      plain[0].setAttribute('data-cart-express-buttons', '');
      home.appendChild(plain[0]);
      plain.slice(1).forEach((node) => node.remove());
    }

    const trigger = this.triggerTemplate ? this.triggerTemplate.cloneNode(true) : null;
    const checkout = ctas.querySelector('.cart__checkout-button');
    if (trigger && checkout) trigger.disabled = checkout.disabled;
    const pieces = trigger ? [home, trigger] : [home];
    if (checkout) checkout.after(...pieces);
    else ctas.append(...pieces);
    return home;
  }

  walletNode(nodes) {
    return (
      nodes.find((node) => node.closest('[data-cart-express-mount]')) ||
      nodes.find((node) => node.querySelector('shopify-accelerated-checkout-cart, #dynamic-checkout-cart')) ||
      nodes[0]
    );
  }

  rebuildWallet(container) {
    const mobile = container.classList.contains(EXPRESS_VERTICAL_CLASS);
    container.querySelector('#dynamic-checkout-cart')?.classList.toggle(EXPRESS_VERTICAL_CLASS, mobile);

    const wallet = container.querySelector('shopify-accelerated-checkout-cart, shopify-accelerated-checkout');
    if (!wallet) return;
    wallet.replaceWith(this.freshWallet(wallet, mobile));
  }

  freshWallet(wallet, mobile) {
    const fresh = document.createElement(wallet.localName);
    for (const attr of wallet.attributes) {
      if (attr.name !== 'class') fresh.setAttribute(attr.name, attr.value);
    }
    const className = (wallet.getAttribute('class') || '')
      .replace(/\badditional-checkout-buttons--(?:vertical|horizontal)\b/g, '')
      .trim();
    if (className) fresh.setAttribute('class', className);
    fresh.classList.toggle(EXPRESS_VERTICAL_CLASS, mobile);
    while (wallet.firstChild) fresh.appendChild(wallet.firstChild);
    return fresh;
  }

  open(trigger) {
    if (!this.mobile) return;
    this.sync();
    const modal = this.modal;
    if (!modal || trigger?.disabled) return;

    this.returnFocus = trigger;
    this.positionCard();
    modal.classList.add('is-open');
    this.drawer.querySelector('.drawer__inner')?.classList.add('is-express-open');
    modal.removeAttribute('inert');
    modal.setAttribute('aria-hidden', 'false');
    this.setInert(true);
    this.focusModal();
    window.dispatchEvent(new Event('resize'));
  }

  close() {
    const modal = this.modal;
    if (!modal?.classList.contains('is-open')) return;

    modal.classList.remove('is-open');
    const inner = this.drawer.querySelector('.drawer__inner');
    inner?.classList.remove('is-express-open');
    inner?.style.removeProperty('--cart-express-shift');
    modal.setAttribute('inert', '');
    modal.setAttribute('aria-hidden', 'true');
    this.setInert(false);

    const returnFocus = this.returnFocus;
    this.returnFocus = null;
    const drawer = document.getElementById('CartDrawer');
    if (typeof trapFocus === 'function' && drawer) {
      trapFocus(drawer, returnFocus?.isConnected ? returnFocus : drawer);
    } else if (returnFocus?.isConnected) {
      returnFocus.focus();
    }
  }

  /*
    The close button is the first focusable control. A sentinel after the
    wallet buttons is the last, so Tab can enter the shadow-DOM buttons
    between them. The theme trap only sees light-DOM controls.
  */
  focusModal() {
    const modal = this.modal;
    const closeBtn = modal?.querySelector('.cart-express__close');
    if (!modal || !closeBtn || typeof trapFocus !== 'function') return;
    if (modal.contains(document.activeElement)) return;
    trapFocus(modal, closeBtn);
  }

  /*
    Rest the card just above the checkout button. Clearing the whole footer
    pins a tall card to the top of the drawer.
  */
  positionCard() {
    const inner = this.drawer.querySelector('.drawer__inner');
    const panel = this.modal?.querySelector('.cart-express__panel');
    const checkout = inner?.querySelector('.cart__ctas');
    if (!inner || !panel) return;

    const gap = 16;
    const innerRect = inner.getBoundingClientRect();
    const checkoutTop = checkout?.getBoundingClientRect().top ?? innerRect.bottom;
    const aboveCheckout = Math.max(0, innerRect.bottom - checkoutTop - gap - 32);
    const maxShift = Math.max(0, inner.clientHeight - panel.offsetHeight - gap * 2);
    inner.style.setProperty('--cart-express-shift', `${Math.min(aboveCheckout, maxShift)}px`);
  }

  setInert(on) {
    this.drawer.querySelectorAll(EXPRESS_INERT_TARGETS).forEach((el) => {
      if (on) el.setAttribute('inert', '');
      else el.removeAttribute('inert');
    });
  }
}
